import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { DownloadFilesService } from '../../services/download-files.service';
import { TokenService } from '../../services/id_token.service';
import { environment } from '../../../environments/environment';
@Component({
  selector: 'app-album-details',
  standalone: true,
  imports: [CommonModule, TranslateModule, PageTitleComponent, LoaderComponent],
  templateUrl: './album-details.component.html',
  styleUrls: ['./album-details.component.css'],
})
export class AlbumDetailsComponent implements OnInit {
  private readonly apiBaseUrl = environment.apiUrl.replace(/\/$/, '');
  albumPhotos: { url: string }[] = [];
  paginatedPhotos: { url: string }[] = [];
  albumName: string = '';

  currentPage = 1;
  photosPerPage = 6;
  totalPages = 0;
  isLoading = false; // 🔹 Loader flag

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private downloadService: DownloadFilesService,
    private tokenService: TokenService,
  ) {}

  ngOnInit(): void {
    // Vérifie que nous sommes dans un navigateur avec localStorage
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      console.warn('localStorage is not available in this environment.');
      return;
    }

    // Récupérer le token depuis l'URL
    const token = this.route.snapshot.paramMap.get('token');

    if (!token) {
      console.error('No token provided in route');
      return;
    }

    // Décrypter le token
    const decryptedData = this.tokenService.decryptToken(token);

    if (!decryptedData) {
      console.error('Invalid or corrupted token');
      return;
    }
    console.log('Decrypted token data:', decryptedData);
    const studentId = Number(decryptedData.studentId);
    const albumId = decryptedData.albumId;
    this.albumName = decryptedData.albumName;

    // Optionnel : récupérer infos de l'élève depuis localStorage si nécessaire
    const studentList = JSON.parse(localStorage.getItem('studentList') || '[]');
    const student = studentList.find((s: any) => s.idStudent === studentId);
    const nameOrg = student ? student.nameOrg : null;

    // URL API pour récupérer les photos de l'album
    const apiUrl =
      `${this.apiBaseUrl}/gallery/listGalleryStudentByStudentAndThemeGallery/${studentId}/${albumId}`.trim();
    const baseDomain = 'https://plateforme.educanet.pro/';

    this.isLoading = true;

    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {
        const photos = response?.response || [];

        // Mapping des photos pour affichage
        this.albumPhotos = photos.map((p: any) => ({
          url: p.galerie?.path
            ? `${baseDomain}${p.galerie.path.replace(/^\/+/, '')}` // nettoie les doubles slash
            : 'https://via.placeholder.com/600x400?text=No+Image',
          title: p.galerie?.title || 'Sans titre',
          creationDate: p.galerie?.creationDate || '',
        }));

        // Pagination si nécessaire
        this.totalPages = Math.ceil(
          this.albumPhotos.length / this.photosPerPage,
        );
        this.updatePaginatedPhotos();

        this.isLoading = false;
        console.log('Album photos loaded for:', this.albumName);
      },
      error: (err) => {
        console.error('Failed to load album photos', err);
        this.isLoading = false;
      },
    });
  }

  updatePaginatedPhotos() {
    const start = (this.currentPage - 1) * this.photosPerPage;
    const end = start + this.photosPerPage;
    this.paginatedPhotos = this.albumPhotos.slice(start, end);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePaginatedPhotos();
    }
  }
  getVisiblePages(): (number | string)[] {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const visiblePages: (number | string)[] = [];

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        visiblePages.push(i);
      }
    } else {
      // Always show first page
      visiblePages.push(1);

      if (currentPage <= 4) {
        // Show pages 2, 3, 4, 5, ..., last
        for (let i = 2; i <= 5; i++) {
          visiblePages.push(i);
        }
        visiblePages.push('...');
        visiblePages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show 1, ..., last-4, last-3, last-2, last-1, last
        visiblePages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          visiblePages.push(i);
        }
      } else {
        // Show 1, ..., current-1, current, current+1, ..., last
        visiblePages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          visiblePages.push(i);
        }
        visiblePages.push('...');
        visiblePages.push(totalPages);
      }
    }

    return visiblePages;
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedPhotos();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePaginatedPhotos();
    }
  }

  /*   downloadFile(filePath: string) {
    const fileName = this.getFileName(filePath);
    console.log('Downloading file:', filePath);
    this.downloadService.downloadFile(filePath, fileName);
  }
 */
  downloadFile(filePath: string): void {
    if (!filePath) {
      console.error('URL du fichier manquante');
      return;
    }

    const fileName = this.getFileName(filePath);
    console.log('Téléchargement:', fileName, 'depuis:', filePath);

    this.downloadService.downloadFile(filePath, fileName).subscribe({
      next: (blob: Blob) => {
        // Créer une URL temporaire pour le Blob
        const blobUrl = window.URL.createObjectURL(blob);

        // Créer un lien de téléchargement
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;

        // Déclencher le téléchargement
        document.body.appendChild(link);
        link.click();

        // Nettoyer les ressources
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);

        console.log('✅ Fichier téléchargé:', fileName);
      },
      error: (err) => {
        console.error('❌ Erreur téléchargement:', err);

        // Gestion des erreurs CORS
        if (err.status === 0) {
          alert("Erreur CORS. Veuillez contacter l'administrateur.");
        } else {
          alert("Erreur lors du téléchargement. Vérifiez l'URL du fichier.");
        }
      },
    });
  }

  getFileName(filePath: string): string {
    if (!filePath) return 'photo.jpg';

    // Nettoyer l'URL
    const cleanUrl = filePath.trim().replace(/\s+/g, '');

    // Extraire le nom du fichier
    let name = cleanUrl.split('/').pop() || 'photo.jpg';

    // Supprimer les paramètres de requête et fragments
    name = name.split('?')[0].split('#')[0];

    // Ajouter l'extension si absente
    if (!name.includes('.')) {
      const ext = cleanUrl.match(/\.(jpg|jpeg|png|gif)/i)?.[1] || 'jpg';
      return `photo.${ext}`;
    }

    return name;
  }
}
